import { calculateVatAmount, roundVatToInteger } from './GetVatValue';
import { getIsraelInvoiceAmountLimit } from './GetIsraelInvoiceAmountLimit';
import { parseBeautifiedNumber } from './FormatNumber';

function isExpenseDocType(docType) {
    const type = String(docType || '');
    return type === 'Expenses' || type === 'Expense';
}

function isJournalDocType(docType) {
    const type = String(docType || '');
    return type === 'JournalEntries' || type === 'JournalEntry';
}

function hasAllocationNumber(document) {
    const raw = document?.confirmationNumber ?? document?.ConfirmationNumber;
    const value = String(raw ?? '').trim();
    return value !== '' && value !== '0';
}

/** Same rule as PCN: subject to VAT when the stored vat percentage is not 0. */
function isVatNotZero(document) {
    const vat = Number(document?.vat ?? document?.VAT);
    return Number.isFinite(vat) && vat !== 0;
}

export function getAmountBeforeVat(document) {
    const amount = parseBeautifiedNumber(document?.amount ?? document?.Amount);
    if (amount == null || Number.isNaN(amount)) return null;

    const vatRate = document?.vat ?? document?.VAT;
    const date = document?.documentDate ?? document?.DocumentDate;
    const vatAmt = roundVatToInteger(calculateVatAmount(amount, vatRate, date));
    return amount - vatAmt;
}

/**
 * Expense documents (and VAT-liable journal entries) need an allocation number
 * when the amount before VAT is at/above the Israel invoice limit for the document date.
 *
 * @returns {{ shouldWarn: boolean, limit: number, amountBeforeVat: number|null }}
 */
export function shouldWarnMissingAllocationNumber(document) {
    const empty = { shouldWarn: false, limit: 0, amountBeforeVat: null };
    if (!document) return empty;

    const docType = document.docType || document.documentType;
    if (!isExpenseDocType(docType) && !isJournalDocType(docType)) {
        return empty;
    }

    if (!isVatNotZero(document)) {
        return empty;
    }

    if (hasAllocationNumber(document)) {
        return empty;
    }

    const amountBeforeVat = getAmountBeforeVat(document);
    const date = document.documentDate ?? document.DocumentDate;
    const limit = getIsraelInvoiceAmountLimit(date);
    const shouldWarn =
        amountBeforeVat != null &&
        limit > 0 &&
        amountBeforeVat >= limit;

    return { shouldWarn, limit, amountBeforeVat };
}
